import { useEffect, useId, useRef, useState } from "react";
import "./ConfirmationGate.css";

export interface ConfirmationGateProps {
  open: boolean;
  toolName: string;
  consequence: string;
  /** Si se define, el visitante debe teclearla exactamente para habilitar "Confirmar". */
  confirmPhrase?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Puerta de confirmación para acciones sensibles/irreversibles (P3-R2). Usa el elemento
 * nativo <dialog>: foco atrapado, cierre con Escape y rol de diálogo los da el navegador,
 * sin reconstruir ese comportamiento a mano con ARIA.
 */
export function ConfirmationGate({
  open,
  toolName,
  consequence,
  confirmPhrase,
  onConfirm,
  onCancel,
}: ConfirmationGateProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [typed, setTyped] = useState("");
  const inputId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      setTyped("");
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleCancel = (event: Event): void => {
      event.preventDefault();
      onCancel();
    };
    dialog.addEventListener("cancel", handleCancel);
    return () => dialog.removeEventListener("cancel", handleCancel);
  }, [onCancel]);

  const requiresTypedPhrase = Boolean(confirmPhrase);
  const canConfirm = !requiresTypedPhrase || typed === confirmPhrase;

  return (
    <dialog
      ref={dialogRef}
      className="confirmation-gate"
      aria-labelledby="confirmation-gate-heading"
    >
      <form method="dialog" className="confirmation-gate__form">
        <h3 id="confirmation-gate-heading" className="confirmation-gate__heading">
          <span aria-hidden="true">⚠️</span> Confirma una acción sensible
        </h3>
        <p className="confirmation-gate__tool">
          Herramienta: <span className="mono">{toolName}</span>
        </p>
        <p className="confirmation-gate__consequence">{consequence}</p>
        {requiresTypedPhrase && (
          <p className="confirmation-gate__field">
            <label htmlFor={inputId}>
              Escribe <strong className="mono">{confirmPhrase}</strong> para continuar
            </label>
            <input
              id={inputId}
              type="text"
              autoComplete="off"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </p>
        )}
        <div className="confirmation-gate__actions">
          <button type="button" className="btn btn--ghost" onClick={onCancel} autoFocus>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--danger"
            onClick={onConfirm}
            disabled={!canConfirm}
          >
            Confirmar y ejecutar
          </button>
        </div>
      </form>
    </dialog>
  );
}
